import { Link } from "react-router-dom";
import { api, type MyWork, type AssignmentOffer } from "../lib/api";
import { useResource, useAction } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import { Region, Entry, RegisterLayout, Apparatus, MarginNote } from "../components/Register";
import {
  AbsentNotice,
  Button,
  Empty,
  Failed,
  Figure,
  Mark,
  SkeletonRows,
} from "../components/primitives";
import { day, relativeDay, humanise } from "../lib/format";

/**
 * The consultant's workspace.
 *
 * A consultant is an occasional user on an unknown device, so this surface has
 * to be usable without being taught. It answers two questions and refuses to
 * become a project browser: what is mine today, and what has been asked of me.
 *
 * The scope note in the margin is load-bearing rather than decorative. A
 * consultant's access begins on acceptance and ends at a timestamp, and someone
 * whose assignment lapses mid-week should be able to see why the work vanished
 * rather than assuming the platform broke.
 */
export function Workspace() {
  const { resource, reload } = useResource<MyWork>(() => api.get<MyWork>("/api/my-work"), []);

  if (resource.state === "loading") {
    return (
      <AppShell>
        <ScreenHead title="My work" />
        <SkeletonRows rows={5} />
      </AppShell>
    );
  }

  if (resource.state === "absent") {
    return (
      <AppShell>
        <ScreenHead title="My work" />
        <AbsentNotice />
      </AppShell>
    );
  }

  if (resource.state === "failed") {
    return (
      <AppShell>
        <ScreenHead title="My work" />
        <Failed message={resource.message} correlationId={resource.correlationId} onRetry={reload} />
      </AppShell>
    );
  }

  const work = resource.data;
  const overdue = work.today.filter((t) => t.overdue).length;

  return (
    <AppShell>
      {/* "Submit completed work" used to sit here, rendered with no
          handler, and it is removed rather than wired.

          There is nothing it could honestly have done. Submitting work
          means submitting a VERSION OF A DELIVERABLE: it needs an
          engagement, a deliverable within it, and a file from that
          engagement's drive. This screen is deliberately not a project
          browser — it spans every engagement a person is on — so a global
          "submit" here would have to invent a three-step picker that
          duplicates Work and gets the file list wrong.

          Each item below already leads to where the work is done. A button
          that promises an action the model does not have is worse than no
          button, which is what it had been demonstrating since it shipped. */}
      <ScreenHead
        title="Your work"
        sub={`${work.today.length} ${work.today.length === 1 ? "item needs" : "items need"} your attention today.`}
      />

      <RegisterLayout
        margin={
          <Apparatus>
            <div className="flex gap-7 xl:gap-8">
              <Figure value={work.today.length} label="open tasks" />
              <Figure
                value={overdue}
                label="overdue"
                tone={overdue > 0 ? "danger" : "muted"}
              />
            </div>

            {work.context.map((ctx) => (
              <MarginNote key={ctx.engagementId} title="Engagement details">
                <span className="text-ink">{ctx.engagement}</span>
                <br />
                Access: {ctx.scope === "engagement"
                  ? "Whole engagement"
                  : ctx.scope === "workstream"
                    ? "One workstream"
                    : "Assigned tasks only"}
                {ctx.lead && <> &middot; Lead {ctx.lead}</>}
                {ctx.expiresAt && (
                  <>
                    <br />
                    <span className="text-warning">
                      Access ends {day(ctx.expiresAt)} ({relativeDay(ctx.expiresAt)})
                    </span>
                  </>
                )}
              </MarginNote>
            ))}

            {work.context.length === 0 && (
              <MarginNote title="Your work">
                Nothing live right now. Work you accept shows here, with the
                date it runs until.
              </MarginNote>
            )}
          </Apparatus>
        }
      >
        <Region title="Today" count={work.today.length}>
          {work.today.length === 0 ? (
            <Empty title="Nothing is assigned to you">
              Once you accept a piece of work, your tasks for it appear here.
            </Empty>
          ) : (
            work.today.map((task) => (
              <Entry
                key={task.taskId}
                lead={task.title}
                meta={
                  <>
                    {task.engagement}
                    {task.dueOn && <> &middot; due {day(task.dueOn)}</>}
                  </>
                }
                right={
                  <>
                    {task.overdue && <Mark tone="danger">{relativeDay(task.dueOn)}</Mark>}
                    <Mark tone={task.status === "in_progress" ? "live" : "rest"}>
                      {humanise(task.status)}
                    </Mark>
                  </>
                }
              >
                {/* `task.body` is what the work actually is, and this
                    query never selected it — so a consultant saw a
                    one-line title and a date and had no way to learn what
                    was being asked of them. Shown inline rather than
                    behind a click: a task's detail is usually a sentence,
                    and hiding a sentence behind a disclosure is a worse
                    trade than the length it costs. */}
                {task.body && (
                  <p className="max-w-[70ch] whitespace-pre-line pb-3 text-sm text-secondary">
                    {task.body}
                  </p>
                )}
              </Entry>
            ))
          )}
        </Region>

        <Region title="Revisions requested" count={work.revisionsRequested.length}>
          {work.revisionsRequested.length === 0 ? (
            <Empty title="No revisions outstanding">
              If someone asks for changes to work you submitted, you will find
              their note here with the version it refers to.
            </Empty>
          ) : (
            work.revisionsRequested.map((rev) => (
              <Entry
                key={`${rev.deliverableId}-${rev.versionNo}`}
                lead={rev.title}
                meta={
                  <>
                    <span className="font-mono">v{rev.versionNo}</span> &middot;{" "}
                    {rev.requestedBy} asked: &ldquo;{rev.body}&rdquo;
                  </>
                }
                // A link, not a button, and deliberately so.
                //
                // Making the changes means submitting a new version, and a
                // version is a file from that engagement's drive — chosen
                // from a list this screen does not have and should not
                // fetch, since "My work" spans every engagement. Work
                // already does it properly, per deliverable, with the file
                // picker beside the history.
                //
                // So this goes there. A control that takes you to the
                // place that does the job is honest; one that half-does it
                // in a second location is how two version threes arrive.
                right={
                  <Link
                    to={`/engagements/${rev.engagementId}/work`}
                    className="inline-flex min-h-11 items-center border border-ink px-4 text-sm text-ink transition-colors duration-150 hover:bg-ink hover:text-canvas"
                  >
                    Make changes
                  </Link>
                }
              />
            ))
          )}
        </Region>
      </RegisterLayout>
    </AppShell>
  );
}

/**
 * Offers.
 *
 * Separate from the workspace on purpose: an unaccepted assignment grants
 * nothing at all — not the engagement's name beyond this screen, not its
 * documents, nothing. Accepting is the act that creates access, so it gets its
 * own surface rather than being a row in a list.
 */
export function Offers() {
  const { resource, reload } = useResource<AssignmentOffer[]>(
    () => api.get<AssignmentOffer[]>("/api/my-offers"),
    [],
  );

  const accept = useAction(async (id: string) => {
    await api.post(`/api/assignments/${id}/accept`);
  });
  const decline = useAction(async (id: string) => {
    await api.post(`/api/assignments/${id}/decline`);
  });

  if (resource.state === "loading") {
    return (
      <AppShell>
        <ScreenHead title="Offers" />
        <SkeletonRows rows={2} />
      </AppShell>
    );
  }

  if (resource.state === "failed") {
    return (
      <AppShell>
        <ScreenHead title="Offers" />
        <Failed message={resource.message} correlationId={resource.correlationId} onRetry={reload} />
      </AppShell>
    );
  }

  const offers = resource.state === "ready" ? resource.data : [];
  const refusal = accept.refusal ?? decline.refusal;

  return (
    <AppShell>
      <ScreenHead
        title="Offers"
        sub="Work you have been offered. Accept one and it opens up for you."
      />

      {refusal && (
        <p role="alert" className="mb-6 border border-danger/40 bg-danger/[0.08] px-3 py-2 text-xs text-danger">
          {refusal.message}
          {refusal.correlationId && (
            <span className="ml-2 font-mono text-muted">Reference {refusal.correlationId}</span>
          )}
        </p>
      )}

      <Region title="Awaiting your answer" count={offers.length}>
        {offers.length === 0 ? (
          <Empty title="No offers">
            When you are offered a piece of work it will appear here, with
            what it involves and how long it runs.
          </Empty>
        ) : (
          offers.map((offer) => (
            <div key={offer.id} className="register-entry py-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 max-w-[52ch]">
                  <p className="font-display text-md font-medium tracking-title text-ink">
                    {offer.engagement}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {offer.scope === "engagement"
                      ? "Whole engagement"
                      : offer.scope === "workstream"
                        ? "One workstream"
                        : "Assigned tasks only"}{" "}
                    &middot; starts {day(offer.startsAt)}
                    {offer.expiresAt && <> &middot; ends {day(offer.expiresAt)}</>}
                  </p>
                  {offer.brief && (
                    <p className="mt-3 text-base leading-[1.3rem] text-secondary">
                      {offer.brief}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    tone="quiet"
                    loading={decline.pending}
                    onClick={() => void decline.invoke(offer.id).then((ok) => ok && reload())}
                  >
                    Decline
                  </Button>
                  <Button
                    tone="primary"
                    loading={accept.pending}
                    onClick={() => void accept.invoke(offer.id).then((ok) => ok && reload())}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </Region>
    </AppShell>
  );
}
